/**
 * 错误码 —— 前后端共用的契约。
 *
 * 原则(docs/09-api.md §9.1):
 *   - 跨租户访问返回 404 而不是 403,不泄露资源是否存在
 *   - 司机端**不显示错误码**,按 code 映射成人话
 *   - details 里带足够的信息让客户端自己决定下一步
 */

export const ERROR_CODES = {
  // 400
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  /**
   * 对象存储还不能用,所以这件依赖它的事现在做不了。
   *
   * ★ 单独一个码,不复用 VALIDATION_FAILED:客户端要据此
   *   **把那个开关退回去**并指路到「设置 → 对象存储」。
   *   靠匹配错误文案来做这件事,改一个字就失效。
   */
  STORAGE_NOT_READY: 'STORAGE_NOT_READY',

  // 401
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  PASSWORD_CHANGE_REQUIRED: 'PASSWORD_CHANGE_REQUIRED',

  // 403
  PERMISSION_DENIED: 'PERMISSION_DENIED',

  // 404 —— 跨租户访问也返回这个
  NOT_FOUND: 'NOT_FOUND',

  // 409
  /** 通用的「已存在 / 状态冲突」—— 配置类资源重名走这个 */
  RESOURCE_CONFLICT: 'RESOURCE_CONFLICT',
  INVALID_TRANSITION: 'INVALID_TRANSITION',
  ORDER_MODIFIED: 'ORDER_MODIFIED',
  DUPLICATE_EXTERNAL_REF: 'DUPLICATE_EXTERNAL_REF',
  /**
   * 这个收件人今天已经有一张没走完的单了 —— **问一句,不是拦住**。
   *
   * ★ 和 DUPLICATE_EXTERNAL_REF 不是一回事:那个是「这张单我收过了」,
   *   确定的重复;这个只是「看起来像重复」—— 同一个人一天两单
   *   本来就可能发生(上午一单下午一单、两个不同的包裹)。
   *   所以它不是终点,是一个要人回答的问题:带上 confirmDuplicate 再发就建。
   */
  DUPLICATE_ORDER_SUSPECTED: 'DUPLICATE_ORDER_SUSPECTED',
  NOT_YOUR_ORDER: 'NOT_YOUR_ORDER',
  ORDER_ALREADY_TAKEN: 'ORDER_ALREADY_TAKEN',
  ORDER_ALREADY_SETTLED: 'ORDER_ALREADY_SETTLED',

  // 422 —— 业务规则拒绝
  BUSINESS_RULE_VIOLATED: 'BUSINESS_RULE_VIOLATED',
  PICKUP_PROOF_REQUIRED: 'PICKUP_PROOF_REQUIRED',
  DELIVERY_PROOF_REQUIRED: 'DELIVERY_PROOF_REQUIRED',
  SIGNATURE_REQUIRED: 'SIGNATURE_REQUIRED',
  GEOFENCE_VIOLATION: 'GEOFENCE_VIOLATION',
  SCAN_REQUIRED: 'SCAN_REQUIRED',
  FAIL_REASON_REQUIRED: 'FAIL_REASON_REQUIRED',
  GOODS_DISPOSITION_REQUIRED: 'GOODS_DISPOSITION_REQUIRED',
  DRIVER_AT_CAPACITY: 'DRIVER_AT_CAPACITY',
  DRIVER_NOT_VERIFIED: 'DRIVER_NOT_VERIFIED',
  DRIVER_OFFLINE: 'DRIVER_OFFLINE',
  GRAB_NOT_ALLOWED: 'GRAB_NOT_ALLOWED',
  OUTSIDE_SHIFT: 'OUTSIDE_SHIFT',
  COD_NOT_COLLECTED: 'COD_NOT_COLLECTED',
  SPECIAL_ITEMS_NOT_HANDED_OVER: 'SPECIAL_ITEMS_NOT_HANDED_OVER',
  MAX_ATTEMPTS_REACHED: 'MAX_ATTEMPTS_REACHED',

  // 429
  RATE_LIMITED: 'RATE_LIMITED',

  // 5xx
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  UPSTREAM_ERROR: 'UPSTREAM_ERROR',
  CIRCUIT_OPEN: 'CIRCUIT_OPEN',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export const ERROR_HTTP_STATUS: Record<ErrorCode, number> = {
  VALIDATION_FAILED: 400,
  STORAGE_NOT_READY: 400,

  UNAUTHENTICATED: 401,
  TOKEN_EXPIRED: 401,
  PASSWORD_CHANGE_REQUIRED: 401,

  PERMISSION_DENIED: 403,

  NOT_FOUND: 404,

  RESOURCE_CONFLICT: 409,
  INVALID_TRANSITION: 409,
  ORDER_MODIFIED: 409,
  DUPLICATE_EXTERNAL_REF: 409,
  DUPLICATE_ORDER_SUSPECTED: 409,
  NOT_YOUR_ORDER: 409,
  ORDER_ALREADY_TAKEN: 409,
  ORDER_ALREADY_SETTLED: 409,

  BUSINESS_RULE_VIOLATED: 422,
  PICKUP_PROOF_REQUIRED: 422,
  DELIVERY_PROOF_REQUIRED: 422,
  SIGNATURE_REQUIRED: 422,
  GEOFENCE_VIOLATION: 422,
  SCAN_REQUIRED: 422,
  FAIL_REASON_REQUIRED: 422,
  GOODS_DISPOSITION_REQUIRED: 422,
  DRIVER_AT_CAPACITY: 422,
  DRIVER_NOT_VERIFIED: 422,
  DRIVER_OFFLINE: 422,
  GRAB_NOT_ALLOWED: 422,
  OUTSIDE_SHIFT: 422,
  COD_NOT_COLLECTED: 422,
  SPECIAL_ITEMS_NOT_HANDED_OVER: 422,
  MAX_ATTEMPTS_REACHED: 422,

  RATE_LIMITED: 429,

  INTERNAL_ERROR: 500,
  UPSTREAM_ERROR: 502,
  CIRCUIT_OPEN: 503,
};

/**
 * 司机端文案的 i18n key。
 *
 * ★ 司机永远不该看到 INVALID_TRANSITION 这种东西。他要看到的是
 * 「这单已经被调度员转给其他司机了」以及**下一步该做什么**。
 * 见 docs/14-driver-ux-spec.md §14.7。
 */
export const DRIVER_MESSAGE_KEYS: Partial<Record<ErrorCode, string>> = {
  INVALID_TRANSITION: 'error.driver.orderStateChanged',
  NOT_YOUR_ORDER: 'error.driver.notYourOrder',
  ORDER_ALREADY_TAKEN: 'error.driver.tooSlow',
  ORDER_MODIFIED: 'error.driver.orderChanged',
  PICKUP_PROOF_REQUIRED: 'error.driver.pickupPhotoRequired',
  DELIVERY_PROOF_REQUIRED: 'error.driver.deliveryPhotoRequired',
  SIGNATURE_REQUIRED: 'error.driver.signatureRequired',
  GEOFENCE_VIOLATION: 'error.driver.tooFarFromLocation',
  FAIL_REASON_REQUIRED: 'error.driver.selectFailReason',
  GOODS_DISPOSITION_REQUIRED: 'error.driver.tellUsWhereGoodsAre',
  DRIVER_AT_CAPACITY: 'error.driver.atCapacity',
  DRIVER_NOT_VERIFIED: 'error.driver.documentsNotVerified',
  DRIVER_OFFLINE: 'error.driver.goOnlineFirst',
  COD_NOT_COLLECTED: 'error.driver.codRequired',
  SPECIAL_ITEMS_NOT_HANDED_OVER: 'error.driver.specialItemsRequired',
  RATE_LIMITED: 'error.driver.tooManyRequests',
  UNAUTHENTICATED: 'error.driver.sessionExpired',
  TOKEN_EXPIRED: 'error.driver.sessionExpired',
};

/** 这些错误重试没用,客户端不该放进 outbox 反复重发 */
export const NON_RETRYABLE: readonly ErrorCode[] = [
  ERROR_CODES.VALIDATION_FAILED,
  // 重试解决不了「存储没配好」—— 要人去配
  ERROR_CODES.STORAGE_NOT_READY,
  ERROR_CODES.PERMISSION_DENIED,
  ERROR_CODES.NOT_FOUND,
  ERROR_CODES.INVALID_TRANSITION,
  ERROR_CODES.NOT_YOUR_ORDER,
  ERROR_CODES.ORDER_ALREADY_TAKEN,
  ERROR_CODES.ORDER_ALREADY_SETTLED,
  ERROR_CODES.DUPLICATE_EXTERNAL_REF,
  ERROR_CODES.PICKUP_PROOF_REQUIRED,
  ERROR_CODES.DELIVERY_PROOF_REQUIRED,
  ERROR_CODES.SIGNATURE_REQUIRED,
  ERROR_CODES.FAIL_REASON_REQUIRED,
  ERROR_CODES.GOODS_DISPOSITION_REQUIRED,
  ERROR_CODES.MAX_ATTEMPTS_REACHED,
  /*
   * ★ 特殊物品没确认,重发一模一样的请求还是不会过 ——
   *   缺的是司机在门口的一个动作,不是网络。
   *   当成网络错误退避重试的话,他以为送达成功了走人,
   *   而那箱冻品还在车上化着。
   */
  ERROR_CODES.SPECIAL_ITEMS_NOT_HANDED_OVER,
  /*
   * ★ 电子围栏不该重试。
   *
   *   重试发的是**一模一样的请求** —— 而它失败的原因是这个请求里
   *   压根没有定位(司机没给位置权限),或者他人还没到店。
   *   同一份数据再发十次,十次都会被同样地拒绝。
   *
   *   放在这里之前,它被当成网络类错误退避重试:司机点完「确认取货」
   *   屏幕跳回列表,他以为成了,而订单一直没动 ——
   *   系统里没有任何地方说过「你得先开定位」。
   *   这条是用户报「司机无法更新订单状态」的真正原因。
   */
  ERROR_CODES.GEOFENCE_VIOLATION,
];

export function isRetryable(code: ErrorCode): boolean {
  return !NON_RETRYABLE.includes(code);
}

/**
 * 冲突类错误 —— 司机端离线队列遇到这些时,不是重试,
 * 而是拉取最新状态覆盖本地并明确告知司机。
 */
export const CONFLICT_CODES: readonly ErrorCode[] = [
  ERROR_CODES.INVALID_TRANSITION,
  ERROR_CODES.NOT_YOUR_ORDER,
  ERROR_CODES.ORDER_ALREADY_TAKEN,
  ERROR_CODES.ORDER_MODIFIED,
];

export function isConflict(code: ErrorCode): boolean {
  return CONFLICT_CODES.includes(code);
}

export interface ApiErrorBody {
  error: {
    code: ErrorCode;
    message: string;
    details?: Record<string, unknown>;
    requestId?: string;
    traceId?: string;
  };
}

/** 业务异常基类。API 层的过滤器把它转成 ApiErrorBody */
export class AppError extends Error {
  constructor(
    readonly code: ErrorCode,
    message?: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message ?? code);
    this.name = 'AppError';
  }

  get httpStatus(): number {
    return ERROR_HTTP_STATUS[this.code];
  }

  get retryable(): boolean {
    return isRetryable(this.code);
  }

  toBody(requestId?: string, traceId?: string): ApiErrorBody {
    return {
      error: {
        code: this.code,
        message: this.message,
        ...(this.details ? { details: this.details } : {}),
        ...(requestId ? { requestId } : {}),
        ...(traceId ? { traceId } : {}),
      },
    };
  }
}
